import { defineAuthProvider } from '@zg-rentals/auth-react/legacy';
import { createAuthPredicates } from '@zg-rentals/auth-comet/mfa';
import { getClient } from '@zg-rentals/http-client';
import { createContext, type ReactNode, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/hooks';
import type { RootState } from '../../../store/types';
import UserModel from '@common/models/User';
import UserActions from '@common/actions/UserActions';

export type RenterUser = ReturnType<typeof UserModel>;

type RenterAuthProps = {
  enabled: boolean;
  initialUser: RenterUser;
};

const USER_API_URL = __DEV__
  ? '/renter-hub/payments/proxy/renter-hub-api/api/v1/users/get'
  : '/renter-hub-api/api/v1/users/get';

const renterAuthPredicates = createAuthPredicates<RenterUser>((user) => ({
  ...user,
  isLoggedIn: user.loggedIn,
  hasStrongAuthentication: user.hasStrongAuthentication && user.sessionExpirationDate >= Date.now(),
}));

export const isRenterBlocked = (user: RenterUser) =>
  user.loggedIn && user.isUserBlocked && !(user.impersonatorEmail && user.impersonatorUserToken);

export const hasRenterStrongAuth = renterAuthPredicates.hasStrongAuth;

export const isRenterAuthorized = (user: RenterUser) => isRenterBlocked(user) || hasRenterStrongAuth(user);

export const RenterAuth = defineAuthProvider<RenterAuthProps, RenterUser>({
  queryKey: ['rent-payment-web', 'renter-auth'],
  queryOptions: ({ enabled, initialUser }) => ({
    queryFn: () =>
      getClient()
        .get(USER_API_URL, {
          searchParams: { pageWithSensitiveInformation: true, includeSummary: true },
        })
        .json<{ success: boolean; response?: Parameters<typeof UserModel>[0] }>()
        .then(({ success, response }) => {
          if (!success || !response) throw new Error('getUser failed');
          return UserModel(response);
        }),
    enabled,
    staleTime: 0,
    initialData: isRenterAuthorized(initialUser) ? initialUser : undefined,
  }),
  isAuthorized: isRenterAuthorized,
});

export const { useAuth: useRenterAuth } = RenterAuth;

function RenterAuthSyncer() {
  const dispatch = useAppDispatch();
  const { user } = useRenterAuth();
  useEffect(() => {
    if (user) {
      dispatch(UserActions.updateUserInfo(user));
    }
  }, [user, dispatch]);
  return null;
}

const RenterAuthEnabledContext = createContext(false);

export const useRenterAuthEnabled = () => useContext(RenterAuthEnabledContext);

export const RenterAuthProvider = ({ children, enabled }: { children: ReactNode; enabled: boolean }) => {
  const user = useSelector((state: RootState) => state.user);
  const initialUser = useMemo(() => UserModel(user), [user]);

  return (
    <RenterAuthEnabledContext.Provider value={enabled}>
      <RenterAuth.Provider enabled={enabled} initialUser={initialUser}>
        <RenterAuthSyncer />
        {children}
      </RenterAuth.Provider>
    </RenterAuthEnabledContext.Provider>
  );
};
